import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV } from '@app/constants/global.constants';
import { Env } from '@app/types/env';
import { JengaError } from '@app/types/error';
import PaginationRequestI from '@data/interfaces/pagination-request';
import PaginationResponseI from '@data/interfaces/pagination-response';
import UserDTO from '@data/models/user/user-dto';
import UserFilterDTO from '@data/models/user/user-filter-dto';
import { getPaginationUrlGetParams } from '@data/utils/pagination-aux';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public userLogged$: BehaviorSubject<UserDTO> = new BehaviorSubject(null);
  private readonly RESTORE_PASSWORD_PATH = '/api/users/forgotPass';
  private readonly CHANGE_PASSWORD_PATH = '/api/users/forgotPassChange';
  private readonly USER_DETAILS_PATH = '/api/users';
  private readonly USER_EDIT_PROFILE_PATH = '/api/users/editProfile';
  private readonly SEARCH_USERS_PATH = '/api/users/search';
  private readonly SEARCH_USERS_LDAP_PATH = '/api/users/ldap';
  private readonly USER_ADD_PATH = '/api/users/save';
  private readonly USER_DELETE_PATH = '/api/users';

  constructor(@Inject(ENV) private env: Env, private http: HttpClient) {}

  public restorePassword(userName: string): Observable<void> {
    return this.http
      .get<void>(`${this.env.apiBaseUrl}${this.RESTORE_PASSWORD_PATH}/${userName}`)
      .pipe(catchError((error) => throwError(error as JengaError)));
  }

  public changePassword(credentials: {
    hash: string;
    userName: string;
    pass: string;
    passConfirmation: string;
  }): Observable<UserDTO> {
    return this.http
      .post<UserDTO>(`${this.env.apiBaseUrl}${this.CHANGE_PASSWORD_PATH}`, credentials)
      .pipe(catchError((error) => throwError(error as JengaError)));
  }

  public getUserDetailsById(userId: string): Observable<UserDTO> {
    return (
      this.http
        .get<UserDTO>(`${this.env.apiBaseUrl}${this.USER_DETAILS_PATH}/${userId}`)
        // .get<UserDTO>(`${this.env.apiBaseUrlExpress}${this.USER_DETAILS_PATH}/${userId}`)
        .pipe(catchError((error) => throwError(error as JengaError)))
    );
  }

  public addUser(userData: UserDTO): Observable<UserDTO> {
    return this.http
      .post<UserDTO>(`${this.env.apiBaseUrl}${this.USER_ADD_PATH}`, userData)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }

  public editUserProfile(userData: {
    login: string;
    nom: string;
    // firstName: string;
    cognoms: string;
    idRol: string;
    nomRol: string;
    // currentPass: string | null;
    // newPass: string | null;
    // newPassConfirmation: string | null;
  }): Observable<UserDTO> {
    return this.http
      .put<UserDTO>(`${this.env.apiBaseUrl}${this.USER_EDIT_PROFILE_PATH}`, userData)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }

  public searchUsers(userFilter: UserFilterDTO, pagination?: PaginationRequestI): Observable<PaginationResponseI<UserDTO>> {
    return this.http
      .post<PaginationResponseI<UserDTO>>(
        `${this.env.apiBaseUrl}${this.SEARCH_USERS_PATH}${getPaginationUrlGetParams(pagination, true)}`,
        // `${this.env.apiBaseUrlExpress}${this.SEARCH_USERS_PATH}${getPaginationUrlGetParams(pagination, true)}`,
        userFilter
      )
      .pipe(catchError((error) => throwError(error as JengaError)));
  }

  public searchUsersInLdap(userFilter: UserFilterDTO): Observable<UserDTO> {
    let url = `${this.env.apiBaseUrl}${this.SEARCH_USERS_LDAP_PATH}?`;
    if (userFilter?.dni) {
      url = `${url}dni=${userFilter.dni}`;
      url = userFilter.login ? `${url}&` : url;
    }
    if (userFilter?.login) {
      url = `${url}login=${userFilter.login}`;
    }
    return this.http.get<UserDTO>(url).pipe(catchError((error) => throwError(error as JengaError)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteUser(userData: UserDTO): Observable<any> {
    return this.http
      .delete(`${this.env.apiBaseUrl}${this.USER_DELETE_PATH}/${userData.login}`)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }

  public editUser(
    userData: { login: string; nom: string; cognoms: string; idRol: string; nomRol: string },
    originalLogin?: string
  ): Observable<UserDTO> {
    return this.http
      .put<UserDTO>(`${this.env.apiBaseUrl}${this.USER_DETAILS_PATH}/${originalLogin ? originalLogin : userData.login}`, userData)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }
}
